import { db } from '@backend/db/db'
import { useDocument } from '@backend/db/hooks/useDocument'
import { logEncounterView, updateEncounterBillingFields } from '@backend/functions/gyn'
import { ArrowBackIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  Spinner,
  Tag,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useApp, useAuth, usePopUpMessage } from '@hb/react'
import {
  AdminPracticeData,
  CLAIMS,
  DEFAULT_EM_THRESHOLDS,
  DEFAULT_INTRAPARTUM_THRESHOLDS,
  DEFAULT_TIME_LOG_FIELD_LABELS,
  EDUCATION_CHECKLIST_LOCKED_ITEMS,
  EmThreshold,
  Encounter,
  ENCOUNTERS,
  EncounterSection,
  EncounterStatus,
  EncounterType,
  encounterTypeName,
  FacilityClaim,
  FieldMapValue,
  GaWindow,
  getDaysSinceDOS,
  getLaborCalendarDates,
  INSURANCE_PROVIDERS,
  InsuranceProvider,
  isSubsequentDayComplete,
  LABOR_CHARTING,
  LaborCharting,
  MIDWIVES_ADMIN,
  PayerCodeRequirement,
  PregnancyClaim,
  PrenatalExamData,
  prenatalExamField,
  SETTINGS,
  suggestEncounterModifiers,
  SupplyLogEntry,
  TIME_LOG_CONFIG,
  TIME_LOG_FIELD_ORDER,
  TimeLogConfig,
  TimeWorkLog,
  UltrasoundScenario,
  User,
  WithId,
} from '@hb/shared'
import { useQuery } from '@hooks/backend/useQuery'
import { useAssessmentExams } from '@hooks/backend/user/useAssessmentExams'
import { useNavigate } from '@hooks/navigation'
import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  query,
  updateDoc,
  where,
} from 'firebase/firestore'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { generateAllSections } from '../../charting/Encounters/EncounterView'
import { PatientStatementsView } from '../../charting/Encounters/EncounterView/PatientStatementsView'
import { AssessmentPlanView } from '../../charting/Encounters/EncounterView/sections/AssessmentPlanView'
import { BirthingCenterAdmissionView } from '../../charting/Encounters/EncounterView/sections/BirthingCenterAdmissionView'
import { ClinicalRationaleView } from '../../charting/Encounters/EncounterView/sections/ClinicalRationaleView'
import { DepressionScreeningView } from '../../charting/Encounters/EncounterView/sections/DepressionScreeningView'
import { DvIpvScreeningView } from '../../charting/Encounters/EncounterView/sections/DvIpvScreeningView'
import { ExamNotesView } from '../../charting/Encounters/EncounterView/sections/ExamNotesView'
import { ExamVitalsView } from '../../charting/Encounters/EncounterView/sections/ExamVitalsView'
import { FieldSectionView } from '../../charting/Encounters/EncounterView/sections/FieldSectionView'
import { FirstObVisitView } from '../../charting/Encounters/EncounterView/sections/FirstObVisitView'
import { GeneralIllnessExamView } from '../../charting/Encounters/EncounterView/sections/GeneralIllnessExamView'
import { LabsView } from '../../charting/Encounters/EncounterView/sections/LabsView'
import { MdmView } from '../../charting/Encounters/EncounterView/sections/MdmView'
import {
  PelvicExamView,
  PhysicalExamView,
} from '../../charting/Encounters/EncounterView/sections/PhysicalExamView'
import { PreVisitContactView } from '../../charting/Encounters/EncounterView/sections/PreVisitContactView'
import { RemoteAssessmentView } from '../../charting/Encounters/EncounterView/sections/RemoteAssessmentView'
import { ReviewOfSystemsView } from '../../charting/Encounters/EncounterView/sections/ReviewOfSystemsView'
import { RiskAssessmentView } from '../../charting/Encounters/EncounterView/sections/RiskAssessmentView'
import { SoapNoteView } from '../../charting/Encounters/EncounterView/sections/SoapNoteView'
import { SubstanceUseCounselingView } from '../../charting/Encounters/EncounterView/sections/SubstanceUseCounselingView'
import { UltrasoundFindingsView } from '../../charting/Encounters/EncounterView/sections/UltrasoundFindingsView'
import { UltrasoundHeaderView } from '../../charting/Encounters/EncounterView/sections/UltrasoundHeaderView'
import { VisitLocationView } from '../../charting/Encounters/EncounterView/sections/VisitLocationView'
import { useEncounterStatusLabel } from '../../charting/Encounters/useEncounterStatusLabel'
import { ReadOnlyPatientFlags } from '../../charting/Pregnancy/LaborCharting/ReadOnlyPatientFlags'
import { SupplyLogReview } from './SupplyLogReview'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getSuggestedCode = (
  totalMinutes: number | undefined,
  thresholds: EmThreshold[],
): EmThreshold | null => {
  if (!totalMinutes) return null
  const sorted = [...thresholds].sort((a, b) => b.minMinutes - a.minMinutes)
  return sorted.find(t => totalMinutes >= t.minMinutes) ?? null
}

const formatMinutes = (minutes: number | undefined) => (minutes != null ? `${minutes} min` : '—')

// ─── Time log summary ─────────────────────────────────────────────────────────

const TimeLogSummary = ({
  timeWorkLog,
  fieldLabels,
}: {
  timeWorkLog: TimeWorkLog
  fieldLabels: Partial<Record<string, string>>
}) => {
  const entries = TIME_LOG_FIELD_ORDER.filter(key => timeWorkLog[key] != null)

  if (entries.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400" fontStyle="italic">
        No time log recorded
      </Text>
    )
  }

  return (
    <VStack spacing={0} align="stretch" w="100%">
      {entries.map((key, i) => (
        <Box key={key}>
          {i > 0 && <Divider borderColor="gray.100" />}
          <HStack px={1} py={1} justify="space-between">
            <Text fontSize="sm" color="gray.600">
              {fieldLabels[key] ?? DEFAULT_TIME_LOG_FIELD_LABELS[key]}
            </Text>
            <Text fontSize="sm" fontWeight={500} color="gray.800" flexShrink={0} ml={2}>
              {formatMinutes(timeWorkLog[key])}
            </Text>
          </HStack>
        </Box>
      ))}
      <Divider borderColor="gray.300" />
      <HStack px={1} py={1.5} justify="space-between" bg="gray.50" borderRadius={4}>
        <Text fontSize="sm" fontWeight={600} color="gray.700">
          Total
        </Text>
        <Text fontSize="sm" fontWeight={700} color="gray.800">
          {formatMinutes(timeWorkLog.totalMinutes)}
        </Text>
      </HStack>
    </VStack>
  )
}

// ─── §4.3: Intrapartum time log section (billing team only) ──────────────────
// Per §0.3: AMA code mapping is never shown to the clinical provider.
// This section is in billing team claim review only.

const IntrapartumTimeLogSection = ({
  laborCharting,
  intrapartumThresholds,
}: {
  laborCharting: LaborCharting | null
  intrapartumThresholds: import('@hb/shared').IntrapartumThreshold[]
}) => {
  const timeLog = laborCharting?.intrapartumTimeLog
  if (!timeLog) {
    return (
      <Text fontSize="sm" color="gray.400" fontStyle="italic">
        No intrapartum time log recorded
      </Text>
    )
  }

  const totalMinutes = timeLog.totalIntrapartumMinutes ?? 0
  const formattedTotal =
    totalMinutes > 0 ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m` : '—'

  const matchingThreshold =
    totalMinutes > 0
      ? intrapartumThresholds.find(
          t => totalMinutes >= t.minMinutes && totalMinutes <= t.maxMinutes,
        )
      : null

  return (
    <VStack spacing={1} align="stretch">
      <HStack px={1} py={0.5} justify="space-between">
        <Text fontSize="sm" color="gray.600" fontWeight={600}>
          Total Intrapartum Time
        </Text>
        <Text fontSize="sm" fontWeight={700} color="gray.800" _dark={{ color: 'gray.100' }}>
          {formattedTotal}
        </Text>
      </HStack>
      <HStack px={1} py={0.5} justify="space-between">
        <Text fontSize="sm" color="gray.600">
          Attendance periods
        </Text>
        <Text fontSize="sm">{timeLog.attendancePeriods?.length ?? 0}</Text>
      </HStack>
      {matchingThreshold && (
        <Box px={1} py={1} bg="purple.50" borderRadius={4} _dark={{ bg: 'purple.900' }}>
          <Text fontSize="xs" fontWeight={600} color="purple.600" _dark={{ color: 'purple.200' }}>
            SUGGESTED CODE — BILLING TEAM REVIEW REQUIRED
          </Text>
          <Text fontSize="sm" color="purple.700" _dark={{ color: 'purple.100' }}>
            {matchingThreshold.cptCode} — {matchingThreshold.label} ({matchingThreshold.minMinutes}–
            {matchingThreshold.maxMinutes} min)
          </Text>
        </Box>
      )}
    </VStack>
  )
}

// ─── §2.8.4 / §2.8.5: Additional billing flags — billing team only ────────────
// These flags (96127, 99401) must NEVER appear in any provider-facing component.
// Computed from encounter data; no new stored fields required.

const AdditionalBillingFlags = ({ data }: { data: FieldMapValue }) => {
  const depression = data?.depressionScreening as { addressed?: boolean } | undefined
  const substance = data?.substanceUseCounseling as { counselingMinutes?: number } | undefined

  const flag96127 = depression?.addressed === true
  const flag99401 = (substance?.counselingMinutes ?? 0) >= 15

  if (!flag96127 && !flag99401) return null

  return (
    <Box bg="amber.50" border="1px solid" borderColor="yellow.300" borderRadius={6} px={3} py={2}>
      <Text fontSize="xs" fontWeight={600} color="yellow.800" letterSpacing="0.5px" mb={2}>
        POSSIBLE ADDITIONAL CODES — REVIEW
      </Text>
      <VStack align="flex-start" spacing={1}>
        {flag96127 && (
          <HStack>
            <Badge colorScheme="yellow" fontSize="xs" px={1.5}>
              96127
            </Badge>
            <Text fontSize="xs" color="yellow.900">
              Depression/anxiety screening documented — may be separately billable
            </Text>
          </HStack>
        )}
        {flag99401 && (
          <HStack>
            <Badge colorScheme="yellow" fontSize="xs" px={1.5}>
              99401
            </Badge>
            <Text fontSize="xs" color="yellow.900">
              Substance use counseling ≥15 min — may be separately billable
            </Text>
          </HStack>
        )}
      </VStack>
      <Text fontSize="2xs" color="yellow.700" mt={1.5}>
        Verify documentation and payer policy before adding additional codes.
      </Text>
    </Box>
  )
}

// ─── Suggested E&M code panel ─────────────────────────────────────────────────

const SuggestedEmCode = ({
  totalMinutes,
  thresholds,
}: {
  totalMinutes: number | undefined
  thresholds: EmThreshold[]
}) => {
  const suggestion = useMemo(
    () => getSuggestedCode(totalMinutes, thresholds),
    [totalMinutes, thresholds],
  )

  if (!totalMinutes) {
    return (
      <Text fontSize="sm" color="gray.400" fontStyle="italic">
        No time log — cannot suggest E&amp;M code
      </Text>
    )
  }

  return (
    <Box
      bg="blue.50"
      border="1px solid"
      borderColor="blue.200"
      borderRadius={6}
      px={3}
      py={2}
      w="100%">
      <Text fontSize="xs" color="blue.600" fontWeight={600} mb={1}>
        SUGGESTED CODE — REVIEW AND CONFIRM
      </Text>
      {suggestion ? (
        <HStack>
          <Badge colorScheme="blue" fontSize="sm" px={2} py={0.5}>
            {suggestion.cptCode}
          </Badge>
          <Text fontSize="sm" color="blue.800" fontWeight={500}>
            {suggestion.label} ({suggestion.minMinutes}–{suggestion.maxMinutes} min)
          </Text>
        </HStack>
      ) : (
        <Text fontSize="sm" color="orange.600">
          {totalMinutes} min total — below minimum threshold. Verify documentation.
        </Text>
      )}
      <Text fontSize="xs" color="blue.500" mt={1.5}>
        Suggested code based on documentation — review and confirm before submitting.
      </Text>
    </Box>
  )
}

// ─── ICD-10 display + inline editing ─────────────────────────────────────────

const Icd10Section = ({
  icd10Codes,
  encounterId,
}: {
  icd10Codes: string[] | undefined
  encounterId: string
}) => {
  const [editing, setEditing] = useState(false)
  const [codes, setCodes] = useState<string[]>([])
  const [newCode, setNewCode] = useState('')
  const [saving, setSaving] = useState(false)
  const { appName } = useApp()
  const { showError } = usePopUpMessage()

  const startEditing = useCallback(() => {
    setCodes(icd10Codes ?? [])
    setNewCode('')
    setEditing(true)
  }, [icd10Codes])

  const removeCode = useCallback(
    (code: string) => setCodes(prev => prev.filter(c => c !== code)),
    [],
  )

  const addCode = useCallback(() => {
    const trimmed = newCode.trim().toUpperCase()
    if (!trimmed || codes.includes(trimmed)) return
    setCodes(prev => [...prev, trimmed])
    setNewCode('')
  }, [newCode, codes])

  // Firestore rules allow no client writes to ENCOUNTERS — persist the denormalized §7.5
  // ICD-10 array via the admin-only callable; surface errors instead of closing the editor
  // as if the save succeeded.
  const save = useCallback(async () => {
    setSaving(true)
    try {
      await updateEncounterBillingFields({ encounterId, icd10Codes: codes, appName })
      setEditing(false)
    } catch (err) {
      showError((err as Error)?.message ?? 'Could not save the ICD-10 codes.')
    } finally {
      setSaving(false)
    }
  }, [encounterId, codes, appName, showError])

  const displayCodes = icd10Codes ?? []

  if (editing) {
    return (
      <Box w="100%">
        <Flex flexWrap="wrap" gap={1} mb={2}>
          {codes.map(code => (
            <Tag key={code} colorScheme="purple" size="sm" borderRadius="full">
              {code}
              <IconButton
                icon={<span style={{ fontSize: 10, lineHeight: 1 }}>✕</span>}
                aria-label={`Remove ${code}`}
                size="xs"
                variant="ghost"
                colorScheme="purple"
                ml={1}
                minW="auto"
                h="auto"
                p={0}
                onClick={() => removeCode(code)}
              />
            </Tag>
          ))}
          {codes.length === 0 && (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              No codes — add one below
            </Text>
          )}
        </Flex>
        <HStack spacing={2} mb={2}>
          <Input
            size="xs"
            placeholder="Add code (e.g. O26.00)"
            value={newCode}
            onChange={e => setNewCode(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCode()}
            maxW="160px"
            bg="white"
          />
          <Button
            size="xs"
            variant="outline"
            colorScheme="purple"
            onClick={addCode}
            isDisabled={!newCode.trim()}>
            Add
          </Button>
        </HStack>
        <HStack spacing={2}>
          <Button size="xs" variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button size="xs" colorScheme="green" isLoading={saving} onClick={save}>
            Save
          </Button>
        </HStack>
      </Box>
    )
  }

  return (
    <Box w="100%">
      <HStack justify="space-between" mb={1}>
        {displayCodes.length > 0 ? (
          <Flex flexWrap="wrap" gap={1} flex={1}>
            {displayCodes.map(code => (
              <Tag key={code} colorScheme="purple" size="sm" borderRadius="full">
                {code}
              </Tag>
            ))}
          </Flex>
        ) : (
          <Text fontSize="sm" color="gray.400" fontStyle="italic" flex={1}>
            No ICD-10 codes on encounter
          </Text>
        )}
        <Button
          size="xs"
          variant="outline"
          colorScheme="purple"
          ml={2}
          flexShrink={0}
          onClick={startEditing}>
          Edit
        </Button>
      </HStack>
    </Box>
  )
}

// ─── Payer requirements checklist ────────────────────────────────────────────

const PayerChecklist = ({
  requirements,
  checked,
  onChange,
}: {
  requirements: string[]
  checked: boolean[]
  onChange: (index: number, value: boolean) => void
}) => (
  <VStack align="stretch" spacing={1}>
    {requirements.map((req, i) => (
      <Checkbox
        key={i}
        isChecked={checked[i] ?? false}
        onChange={e => onChange(i, e.target.checked)}
        colorScheme="green"
        size="sm"
        alignItems="flex-start">
        <Text fontSize="sm" color="gray.700" pt={0.5}>
          {req}
        </Text>
      </Checkbox>
    ))}
  </VStack>
)

// ─── Denial log ───────────────────────────────────────────────────────────────

const DenialLogSection = ({ claim }: { claim: WithId<FacilityClaim> }) => {
  const { authUser } = useAuth()
  const [open, setOpen] = useState(false)
  const [reasonCode, setReasonCode] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const claimRef = useMemo(() => doc(db, CLAIMS, claim.id), [claim.id])

  const submitDenial = useCallback(async () => {
    if (!reasonCode.trim() || !authUser?.uid) return
    setSaving(true)
    const newDenial = {
      on: Date.now(),
      by: authUser.uid,
      reasonCode: reasonCode.trim(),
      notes: notes.trim(),
    }
    const existing = claim.denials ?? []
    await updateDoc(claimRef, { denials: [...existing, newDenial] }).catch(console.error)
    setReasonCode('')
    setNotes('')
    setOpen(false)
    setSaving(false)
  }, [claimRef, claim.denials, authUser, reasonCode, notes])

  const denials = claim.denials ?? []

  return (
    <Box>
      {denials.length > 0 && (
        <VStack align="stretch" spacing={1} mb={2}>
          {denials.map((d, i) => (
            <Box
              key={i}
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
              borderRadius={4}
              px={2}
              py={1}>
              <HStack justify="space-between">
                <Badge colorScheme="red" fontSize="xs">
                  {d.reasonCode}
                </Badge>
                <Text fontSize="xs" color="gray.500">
                  {new Date(d.on).toLocaleDateString()}
                </Text>
              </HStack>
              {d.notes && (
                <Text fontSize="xs" color="gray.600" mt={0.5}>
                  {d.notes}
                </Text>
              )}
            </Box>
          ))}
        </VStack>
      )}
      {open ? (
        <VStack align="stretch" spacing={2} bg="red.50" borderRadius={4} p={2}>
          <Input
            size="sm"
            placeholder="Denial reason code (e.g. CO-4)"
            value={reasonCode}
            onChange={e => setReasonCode(e.target.value)}
            bg="white"
          />
          <Input
            size="sm"
            placeholder="Notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            bg="white"
          />
          <HStack justify="flex-end" spacing={2}>
            <Button size="xs" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="xs"
              colorScheme="red"
              isLoading={saving}
              isDisabled={!reasonCode.trim()}
              onClick={submitDenial}>
              Log Denial
            </Button>
          </HStack>
        </VStack>
      ) : (
        <Button size="xs" variant="outline" colorScheme="red" onClick={() => setOpen(true)}>
          + Log Denial
        </Button>
      )}
    </Box>
  )
}

// ─── §2.10.2: Suggested modifiers/POS panel (billing team only) ──────────────

const SuggestedModifiersPanel = ({ encounter }: { encounter: Encounter }) => {
  const { modifiers, posCode } = suggestEncounterModifiers(encounter)
  if (modifiers.length === 0 && !posCode) return null
  return (
    <Box bg="teal.50" border="1px solid" borderColor="teal.200" borderRadius={6} px={3} py={2}>
      <Text fontSize="xs" fontWeight={600} color="teal.700" letterSpacing="0.5px" mb={2}>
        AUTO-SUGGESTED MODIFIERS — confirm before submission
      </Text>
      <HStack spacing={2} flexWrap="wrap">
        {modifiers.map(m => (
          <Tag key={m} colorScheme="teal" size="sm" fontWeight={600}>
            {m}
          </Tag>
        ))}
        {posCode && (
          <Tag colorScheme="blue" size="sm" fontWeight={600}>
            POS {posCode}
          </Tag>
        )}
      </HStack>
      <Text fontSize="xs" color="teal.600" mt={1.5}>
        Based on visit type and location. Verify against payer requirements before attesting.
      </Text>
    </Box>
  )
}

// ─── Billing rationale + attestation for a single claim ──────────────────────

const ClaimBillingActions = ({
  claim,
  payerRequirements,
  subsequentDayBlocked,
  pocusReportBlocked,
  linkedClaimBlocked,
  deliveryChecklistBlocked,
}: {
  claim: WithId<FacilityClaim>
  payerRequirements: string[]
  subsequentDayBlocked?: boolean
  pocusReportBlocked?: boolean
  linkedClaimBlocked?: boolean
  deliveryChecklistBlocked?: boolean
}) => {
  const { authUser } = useAuth()
  const [rationale, setRationale] = useState(claim.billingRationale ?? '')
  const [saving, setSaving] = useState(false)
  const [attesting, setAttesting] = useState(false)
  const [checklistState, setChecklistState] = useState<boolean[]>(() =>
    Array(payerRequirements.length).fill(false),
  )

  const claimRef = useMemo(() => doc(db, CLAIMS, claim.id), [claim.id])
  const isAttested = !!claim.billingAttestation
  const checklistComplete = checklistState.every(Boolean) || payerRequirements.length === 0

  const handleChecklistChange = useCallback((index: number, value: boolean) => {
    setChecklistState(prev => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const saveRationale = useCallback(async () => {
    setSaving(true)
    await updateDoc(claimRef, { billingRationale: rationale }).catch(console.error)
    setSaving(false)
  }, [claimRef, rationale])

  const attest = useCallback(async () => {
    if (!authUser?.uid) return
    setAttesting(true)
    await updateDoc(claimRef, {
      billingAttestation: {
        by: authUser.uid,
        on: Date.now(),
        name: authUser.displayName ?? authUser.email ?? authUser.uid,
      },
    }).catch(console.error)
    setAttesting(false)
  }, [claimRef, authUser])

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor={isAttested ? 'green.200' : 'gray.200'}
      borderRadius={6}
      px={3}
      py={2}
      w="100%">
      <HStack justify="space-between" mb={2}>
        <Text fontSize="xs" fontWeight={600} color="gray.600" letterSpacing="0.5px">
          CLAIM {claim.id.slice(0, 6).toUpperCase()}
        </Text>
        {isAttested ? (
          <Badge colorScheme="green" fontSize="xs">
            ATTESTED
          </Badge>
        ) : (
          <Badge colorScheme="orange" fontSize="xs">
            PENDING ATTESTATION
          </Badge>
        )}
      </HStack>

      {/* Payer checklist — shown for all claims, hard blocks attestation */}
      {!isAttested && payerRequirements.length > 0 && (
        <Box mb={3}>
          <Text fontSize="xs" color="gray.500" fontWeight={600} mb={1}>
            PAYER CHECKLIST — required before attestation
          </Text>
          <PayerChecklist
            requirements={payerRequirements}
            checked={checklistState}
            onChange={handleChecklistChange}
          />
          {!checklistComplete && (
            <Text fontSize="xs" color="orange.600" mt={1}>
              Complete all checklist items to enable attestation.
            </Text>
          )}
        </Box>
      )}

      {isAttested ? (
        <VStack align="stretch" spacing={2}>
          <Text fontSize="xs" color="green.600">
            Attested by {claim.billingAttestation?.name} on{' '}
            {claim.billingAttestation?.on
              ? new Date(claim.billingAttestation.on).toLocaleDateString()
              : '—'}
          </Text>
          <DenialLogSection claim={claim} />
        </VStack>
      ) : (
        <VStack align="stretch" spacing={2}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Documentation rationale
            </Text>
            <Textarea
              size="sm"
              rows={2}
              value={rationale}
              onChange={e => setRationale(e.target.value)}
              placeholder="One-line documentation rationale..."
              borderRadius={4}
            />
          </Box>
          <HStack justify="flex-end" spacing={2}>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              isLoading={saving}
              onClick={saveRationale}>
              Save
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              isLoading={attesting}
              isDisabled={
                !rationale.trim() ||
                !checklistComplete ||
                !!subsequentDayBlocked ||
                !!pocusReportBlocked ||
                !!linkedClaimBlocked ||
                !!deliveryChecklistBlocked
              }
              title={
                deliveryChecklistBlocked
                  ? 'Complete the delivery billing checklist before attesting'
                  : linkedClaimBlocked
                    ? 'No professional claim linked — create a professional claim before attesting this facility claim'
                    : pocusReportBlocked
                      ? 'POCUS report not authenticated — authenticate the POCUS report first'
                      : subsequentDayBlocked
                        ? 'Subsequent-day documentation incomplete — see panel above'
                        : !checklistComplete
                          ? 'Complete all payer checklist items first'
                          : !rationale.trim()
                            ? 'Enter a documentation rationale'
                            : undefined
              }
              onClick={attest}>
              Attest &amp; Lock
            </Button>
          </HStack>
        </VStack>
      )}
    </Box>
  )
}

// ─── §6.7: Billing team CDI view ─────────────────────────────────────────────

const CdiSection = ({ supplyLog }: { supplyLog: SupplyLogEntry[] }) => {
  if (supplyLog.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400" fontStyle="italic">
        No CDI detections on this encounter.
      </Text>
    )
  }

  const confirmed = supplyLog.filter(e => e.confirmedByProvider)
  const dismissed = supplyLog.filter(e => e.dismissedByProvider)
  const pending = supplyLog.filter(e => !e.confirmedByProvider && !e.dismissedByProvider)

  return (
    <VStack align="stretch" spacing={1} w="100%">
      {confirmed.map(e => (
        <HStack key={e.id} justify="space-between" px={1}>
          <Text fontSize="sm" color="green.700">
            ✓ {e.matchedSupply}
            {e.hcpcsCode ? (
              <Text as="span" fontSize="xs" color="gray.500">
                {' '}
                ({e.hcpcsCode})
              </Text>
            ) : null}
          </Text>
          <Badge colorScheme="green" fontSize="xs">
            Confirmed
          </Badge>
        </HStack>
      ))}
      {dismissed.map(e => (
        <HStack key={e.id} justify="space-between" px={1}>
          <Text fontSize="sm" color="gray.400" textDecoration="line-through">
            {e.matchedSupply}
          </Text>
          <Badge colorScheme="gray" fontSize="xs">
            Dismissed
          </Badge>
        </HStack>
      ))}
      {pending.map(e => (
        <HStack key={e.id} justify="space-between" px={1}>
          <Text fontSize="sm" color="orange.700">
            {e.matchedSupply}
          </Text>
          <Badge colorScheme="orange" fontSize="xs">
            Pending provider review
          </Badge>
        </HStack>
      ))}
    </VStack>
  )
}

// ─── §6.5: Subsequent-day billing status for billing team ─────────────────────

const SubsequentDayBillingSection = ({
  // assessmentId,
  laborCharting,
}: {
  assessmentId: string | null | undefined
  laborCharting: LaborCharting | null
}) => {
  const entries = useMemo(() => Object.values(laborCharting?.entries ?? {}), [laborCharting])

  const calendarDates = useMemo(
    () => getLaborCalendarDates(entries as any, laborCharting?.admission?.admittedAt ?? undefined),
    [entries, laborCharting],
  )

  const subsequentDates = calendarDates.slice(1)
  if (subsequentDates.length === 0) return null

  const notes = laborCharting?.subsequentDayNotes ?? {}

  return (
    <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
      <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.5px" mb={2}>
        SUBSEQUENT-DAY LABOR BILLING (§6.5)
      </Text>
      <VStack align="stretch" spacing={2}>
        {subsequentDates.map(date => {
          const note = notes[date]
          const complete = isSubsequentDayComplete(note)
          const displayDate = new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          })
          return (
            <Box
              key={date}
              bg={complete ? 'green.50' : 'red.50'}
              border="1px solid"
              borderColor={complete ? 'green.200' : 'red.200'}
              borderRadius={4}
              px={2}
              py={1.5}>
              <HStack justify="space-between" mb={0.5}>
                <Text fontSize="sm" fontWeight={600} color={complete ? 'green.700' : 'red.700'}>
                  {displayDate}
                </Text>
                <Badge colorScheme={complete ? 'green' : 'red'} fontSize="xs">
                  {complete ? 'DOCS CONFIRMED' : 'INCOMPLETE'}
                </Badge>
              </HStack>
              {complete ? (
                <Text fontSize="xs" color="green.600">
                  Vitals ✓ · FHR ✓ · Time log ✓
                </Text>
              ) : (
                <VStack align="flex-start" spacing={0} fontSize="xs" color="red.600">
                  {!note?.vitalsConfirmed && <Text>• Maternal vitals not confirmed</Text>}
                  {!note?.fhrConfirmed && <Text>• FHR not confirmed</Text>}
                  {!note?.timeLogConfirmed && <Text>• Time log not confirmed</Text>}
                  <Text mt={1} color="red.700" fontWeight={500}>
                    Provider must confirm documentation before this day can be billed.
                  </Text>
                </VStack>
              )}
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}

// ─── §6.2: Full clinical note — profile-independent section renderer ──────────

const renderSectionForBilling = (
  section: EncounterSection,
  encounter: Encounter,
  data: FieldMapValue,
): ReactNode => {
  switch (section.type) {
    case 'field':
      return <FieldSectionView section={section} data={data} />
    case 'notes':
      return <ExamNotesView data={data} />
    case 'vitals':
      return <ExamVitalsView section={section} data={data} examType={encounter.type} />
    case 'review-of-systems':
      return <ReviewOfSystemsView data={data} />
    case 'risk-assessment':
      return <RiskAssessmentView data={data} />
    case 'visit-location':
      return <VisitLocationView data={data} />
    case 'mdm':
      return <MdmView data={data} />
    case 'first-ob-visit':
      return <FirstObVisitView data={data} />
    case 'clinical-rationale':
      return <ClinicalRationaleView data={data} />
    case 'assessment-plan':
      return <AssessmentPlanView data={data} />
    case 'birthing-center-admission':
      return <BirthingCenterAdmissionView data={data} encounter={encounter} />
    case 'remote-assessment':
      return <RemoteAssessmentView data={data} />
    case 'physical-exam':
      return <PhysicalExamView data={data} />
    case 'pelvic-exam':
      return <PelvicExamView data={data} />
    case 'labs-diagnostics':
      return <LabsView data={data} />
    case 'pre-visit-contact':
      return <PreVisitContactView data={data} />
    case 'prenatal-exam':
      return <FieldSectionView section={{ type: 'field', field: prenatalExamField }} data={data} />
    case 'ultrasound-header':
      return <UltrasoundHeaderView data={data} />
    case 'ultrasound-findings':
      return <UltrasoundFindingsView data={data} />
    case 'soap-note':
      return <SoapNoteView data={data} />
    case 'general-illness-exam':
      return <GeneralIllnessExamView data={data} />
    case 'dv-ipv-screening':
      return <DvIpvScreeningView data={data} />
    case 'depression-screening':
      return <DepressionScreeningView data={data} />
    case 'substance-use-counseling':
      return <SubstanceUseCounselingView data={data} />
    // Profile-dependent or non-rendered sections — not shown in billing read-only view
    // case 'pregnancy-header':
    case 'allergies':
    case 'medications-supplements':
    case 'icd10-selection':
    case 'reason-for-visit':
      return null
  }
}

const EncounterNoteReadOnly = ({ encounter }: { encounter: Encounter }) => {
  const sections = useMemo(() => generateAllSections(encounter, null), [encounter])
  const encounterData = useMemo(() => (encounter.data ?? {}) as FieldMapValue, [encounter])

  const renderedSections = sections
    .map((section: EncounterSection, i: number) => {
      const content = renderSectionForBilling(section, encounter, encounterData)
      if (!content) return null
      return (
        <Box
          key={i}
          py={3}
          px={3}
          borderBottom="1px solid"
          borderColor="gray.100"
          _last={{ borderBottom: 'none' }}>
          {content}
        </Box>
      )
    })
    .filter(Boolean)

  if (renderedSections.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400" fontStyle="italic">
        No structured note content recorded.
      </Text>
    )
  }

  return (
    <VStack align="stretch" spacing={0} w="100%">
      {renderedSections}
    </VStack>
  )
}

// ─── Encounter summary (left panel) ──────────────────────────────────────────

const EncounterSummaryPanel = ({
  encounter,
  encounterId,
  laborCharting,
  patientRef,
}: {
  encounter: Encounter
  encounterId: string
  laborCharting: LaborCharting | null
  patientRef: DocumentReference<User> | null
}) => {
  const amendments = encounter.amendments ?? []
  const statusLabel = useEncounterStatusLabel(encounter.status)

  return (
    <VStack align="stretch" spacing={3} h="100%" overflowY="auto" pr={1}>
      {/* Header */}
      <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
        <HStack justify="space-between" mb={1}>
          <Text fontWeight={600} color="gray.700">
            {encounterTypeName[encounter.type] ?? encounter.type}
          </Text>
          <Badge
            colorScheme={encounter.status === EncounterStatus.AUTHENTICATED ? 'blue' : 'green'}>
            {statusLabel}
          </Badge>
        </HStack>
        <HStack spacing={4} fontSize="sm" color="gray.600">
          <Text>Date: {encounter.date ?? '—'}</Text>
          {encounter.renderingProvider && (
            <Text>
              Provider: {encounter.renderingProviderInitials ?? encounter.renderingProvider}
            </Text>
          )}
        </HStack>
      </Box>

      {/* §0.5.6 Provider flags — read-only for billing team */}
      <ReadOnlyPatientFlags
        assessmentId={encounter.assessmentId}
        practiceId={encounter.practiceId}
        patientRef={patientRef}
      />

      {/* §6.2: Amendment log — full detail */}
      {amendments.length > 0 && (
        <Box bg="white" borderRadius={6} border="1px solid" borderColor="orange.200" px={3} py={2}>
          <HStack mb={2}>
            <AlertIcon color="orange.400" />
            <Text fontSize="xs" fontWeight={600} color="orange.700" letterSpacing="0.5px">
              {amendments.length} AMENDMENT{amendments.length !== 1 ? 'S' : ''} — review before
              attesting
            </Text>
          </HStack>
          <VStack align="stretch" spacing={1}>
            {amendments.map((a, i) => (
              <Box
                key={i}
                bg="orange.50"
                borderRadius={4}
                px={2}
                py={1.5}
                border="1px solid"
                borderColor="orange.100">
                <HStack justify="space-between" mb={0.5}>
                  <Text fontSize="xs" fontWeight={600} color="gray.600">
                    Amendment {i + 1}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {new Date(a.on).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}{' '}
                    {new Date(a.on).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </Text>
                </HStack>
                <Text fontSize="sm" color="gray.700">
                  {a.reason || (
                    <Text as="span" fontStyle="italic" color="gray.400">
                      No reason recorded
                    </Text>
                  )}
                </Text>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {/* §3.5: Admission separately billable notice */}
      {encounter.type === EncounterType.BIRTHING_CENTER_ADMISSION && (
        <Alert status="info" borderRadius={6} fontSize="sm">
          <AlertIcon />
          Admission note — separately billable from the global OB package. Confirm correct claim
          routing before submission.
        </Alert>
      )}

      {/* §2.15.4: Quick Note origin warning */}
      {(encounter.data as any)?.quickNoteOrigin && (
        <Alert status="warning" borderRadius={6} fontSize="sm">
          <AlertIcon />
          <Box>
            <Text fontWeight={600}>Quick Note origin — review for completeness</Text>
            <Text fontSize="xs">
              This note was pre-filled from a Quick Note. Confirm all fields are complete before
              attesting the claim.
            </Text>
          </Box>
        </Alert>
      )}

      {/* §2.4 / §6.2: Weight bypass flag */}
      {(encounter.data as any)?.vital?.weightBypassAcknowledgedAt && (
        <Alert status="warning" borderRadius={6} fontSize="sm">
          <AlertIcon />
          <Box>
            <Text fontWeight={600}>Weight not documented — provider acknowledged</Text>
            <Text fontSize="xs">
              Provider bypassed the weight field at authentication on{' '}
              {new Date(
                (encounter.data as any).vital.weightBypassAcknowledgedAt,
              ).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
              . Weight omission may be flagged in a payer audit — confirm before attesting.
            </Text>
          </Box>
        </Alert>
      )}

      {/* §6.5: Subsequent-day billing panel (only for labor/admission encounters) */}
      {laborCharting && (
        <SubsequentDayBillingSection
          assessmentId={encounter.assessmentId}
          laborCharting={laborCharting}
        />
      )}

      {/* §B.2: Assessment & Plan — shown prominently for billing team review */}
      {(() => {
        const routineAssessment = (encounter.data as any)?.assessment as string | undefined
        const routinePlanItems = (encounter.data as any)?.planItems as
          | Array<{ category?: string; text?: string }>
          | undefined
        const soapAssessment = (encounter.data as any)?.soapNote?.assessment as string | undefined
        const soapPlan = (encounter.data as any)?.soapNote?.plan as string | undefined
        const displayAssessment = routineAssessment || soapAssessment
        const hasPlan =
          !!soapPlan || (Array.isArray(routinePlanItems) && routinePlanItems.length > 0)
        if (!displayAssessment && !hasPlan) return null
        return (
          <Box
            bg="blue.50"
            borderRadius={6}
            border="1px solid"
            borderColor="blue.200"
            px={3}
            py={3}>
            <Text fontSize="xs" fontWeight={700} color="blue.700" letterSpacing="0.5px" mb={2}>
              ASSESSMENT AND PLAN
            </Text>
            {displayAssessment && (
              <Box mb={hasPlan ? 2 : 0}>
                <Text fontSize="xs" color="blue.500" fontWeight={600} mb={1}>
                  Assessment
                </Text>
                <Text fontSize="sm">{displayAssessment}</Text>
              </Box>
            )}
            {hasPlan && (
              <Box>
                <Text fontSize="xs" color="blue.500" fontWeight={600} mb={1}>
                  Plan
                </Text>
                {soapPlan ? (
                  <Text fontSize="sm">{soapPlan}</Text>
                ) : (
                  <VStack align="stretch" spacing={1}>
                    {(routinePlanItems ?? []).map((item, idx) => (
                      <Text key={idx} fontSize="sm">
                        {item.category ? (
                          <Text as="span" fontWeight={600} mr={1}>
                            [{item.category}]
                          </Text>
                        ) : null}
                        {item.text}
                      </Text>
                    ))}
                  </VStack>
                )}
              </Box>
            )}
          </Box>
        )
      })()}

      {/* ICD-10 codes — editable by billing team */}
      <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
        <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.5px" mb={2}>
          ICD-10 CODES
        </Text>
        <Icd10Section icd10Codes={encounter.icd10Codes} encounterId={encounterId} />
      </Box>

      {/* §6.2: Full clinical note — structured note as signed */}
      <Box
        bg="white"
        borderRadius={6}
        border="1px solid"
        borderColor="gray.200"
        px={0}
        py={0}
        overflow="hidden">
        <Text
          fontSize="xs"
          fontWeight={600}
          color="gray.500"
          letterSpacing="0.5px"
          px={3}
          pt={2}
          pb={1}>
          CLINICAL NOTE
        </Text>
        <Divider />
        <EncounterNoteReadOnly encounter={encounter} />
      </Box>

      {/* §6.7: CDI supply log — confirmed items, dismissed items, pending items */}
      <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
        <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.5px" mb={2}>
          CDI — SUPPLY LOG
        </Text>
        <CdiSection supplyLog={encounter.supplyLog ?? []} />
      </Box>

      {/* §I Gap 1: Patient written statements — read only */}
      {patientRef && <PatientStatementsView encounterId={encounterId} patientId={patientRef.id} />}
    </VStack>
  )
}

// ─── §6.8: POCUS credentialing check ─────────────────────────────────────────

const PocusCredentialingCheck = ({
  adminData,
  encounterData,
}: {
  adminData: AdminPracticeData | null
  encounterData: any
}) => {
  const credentialing = adminData?.pocusCredentialing
  const scenarioType: UltrasoundScenario | undefined = encounterData?.scenarioType

  const isExpired = credentialing?.expiresOn
    ? new Date(credentialing.expiresOn) < new Date()
    : false
  const credentialingOk = credentialing?.certified && !isExpired

  return (
    <VStack align="stretch" spacing={2} w="100%">
      {/* Credentialing status */}
      {credentialingOk ? (
        <Alert status="success" borderRadius={4} py={1} fontSize="xs">
          <AlertIcon />
          POCUS credentialing confirmed.
          {credentialing?.expiresOn && (
            <Text as="span" ml={1}>
              Expires: {credentialing.expiresOn}
            </Text>
          )}
        </Alert>
      ) : (
        <Alert status="error" borderRadius={4} py={1} fontSize="xs">
          <AlertIcon />
          <Box>
            <Text fontWeight={600}>
              POCUS credentialing {isExpired ? 'expired' : 'not confirmed'} — verify before
              submitting
            </Text>
            <Text>Update credentialing status in Practice Info before attesting this claim.</Text>
          </Box>
        </Alert>
      )}

      {/* §6.8: TC/PC split billing — shown when encounter is sonographer-study */}
      {scenarioType === 'sonographer-study' && (
        <Box
          bg="purple.50"
          border="1px solid"
          borderColor="purple.200"
          borderRadius={6}
          px={3}
          py={2}>
          <Text fontSize="xs" fontWeight={600} color="purple.700" letterSpacing="0.5px" mb={1}>
            TC/PC SPLIT BILLING REQUIRED
          </Text>
          <VStack align="stretch" spacing={1} fontSize="xs" color="purple.800">
            <Text>Sonographer study detected — this encounter requires two separate claims:</Text>
            <Text>• Technical Component (TC) — billed by facility/sonographer entity</Text>
            <Text>• Professional Component (26) — billed by interpreting provider</Text>
            <Text mt={1} color="purple.600">
              Both claims must be submitted and linked under this encounter. Create each as a
              separate POCUS claim with the appropriate modifier.
            </Text>
          </VStack>
        </Box>
      )}

      {/* §6.8: POCUS report authentication — separate from visit note auth */}
      <Box
        bg={encounterData?.pocusReportAuthentication ? 'green.50' : 'orange.50'}
        border="1px solid"
        borderColor={encounterData?.pocusReportAuthentication ? 'green.200' : 'orange.200'}
        borderRadius={4}
        px={2}
        py={1}>
        <HStack justify="space-between">
          <Text fontSize="xs" fontWeight={600} color="gray.600">
            POCUS Report Authentication
          </Text>
          <Badge
            colorScheme={encounterData?.pocusReportAuthentication ? 'green' : 'orange'}
            fontSize="xs">
            {encounterData?.pocusReportAuthentication ? 'AUTHENTICATED' : 'PENDING'}
          </Badge>
        </HStack>
        {!encounterData?.pocusReportAuthentication && (
          <Text fontSize="xs" color="orange.700" mt={0.5}>
            POCUS report must be authenticated separately before this claim can be attested.
          </Text>
        )}
      </Box>
    </VStack>
  )
}

// ─── §6.4.2: Draft authorization submission ───────────────────────────────────

const DEFAULT_BILLING_ALERT_THRESHOLD = 59

const DraftAuthorizationSection = ({
  encounter,
  examId,
  claims,
  adminData,
}: {
  encounter: Encounter
  examId: string
  claims: WithId<FacilityClaim>[] | null
  adminData: AdminPracticeData | null
}) => {
  const { authUser } = useAuth()
  const { appName } = useApp()
  const { showError } = usePopUpMessage()
  const [submitting, setSubmitting] = useState(false)
  const [localSubmitted, setLocalSubmitted] = useState(false)

  if (encounter.status !== EncounterStatus.COMPLETED) return null

  const alreadySubmitted = !!(encounter as any).draftAuthorizationSubmittedOn
  const authOnFile = adminData?.providerBillingAuthorization?.onFile === true
  const daysSinceDOS = getDaysSinceDOS(encounter.date ?? null)
  const atThreshold = daysSinceDOS !== null && daysSinceDOS >= DEFAULT_BILLING_ALERT_THRESHOLD

  const handleSubmit = async () => {
    if (!authUser?.uid || !claims || claims.length === 0) return
    setSubmitting(true)
    const draftAuth = {
      submittedBy: authUser.uid,
      submittedByName: authUser.displayName ?? authUser.email ?? authUser.uid,
      submittedOn: Date.now(),
    }
    try {
      await Promise.all(
        claims.map(c => updateDoc(doc(db, CLAIMS, c.id), { draftAuthorization: draftAuth })),
      )
      // Firestore rules allow no client writes to ENCOUNTERS — the encounter timestamp goes
      // through the admin-only callable. Only mark submitted after BOTH writes succeed, so a
      // failure doesn't leave the UI claiming a submission that never landed.
      await updateEncounterBillingFields({
        encounterId: examId,
        draftAuthorizationSubmittedOn: draftAuth.submittedOn,
        appName,
      })
      setLocalSubmitted(true)
    } catch (err) {
      showError((err as Error)?.message ?? 'Could not submit under draft authorization.')
    } finally {
      setSubmitting(false)
    }
  }

  if (alreadySubmitted || localSubmitted) {
    const submittedOn = (encounter as any).draftAuthorizationSubmittedOn as number | undefined
    return (
      <Alert status="info" borderRadius={6} fontSize="sm">
        <AlertIcon />
        <Box>
          <Text fontWeight={600}>Draft Authorization — Authentication Pending</Text>
          <Text fontSize="xs">
            {submittedOn
              ? `Submitted ${new Date(submittedOn).toLocaleDateString()}. `
              : 'Submitted. '}
            Provider has been notified. Claim will be re-reviewed when the note is authenticated.
          </Text>
        </Box>
      </Alert>
    )
  }

  if (!authOnFile) {
    return (
      <Alert status="warning" borderRadius={6} fontSize="sm">
        <AlertIcon />
        <Box>
          <Text fontWeight={600}>Draft authorization locked</Text>
          <Text fontSize="xs">
            No Provider Billing Authorization is on file for this practice. Update the authorization
            in Practice Info before submitting under draft authorization.
          </Text>
        </Box>
      </Alert>
    )
  }

  return (
    <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius={6} px={3} py={2}>
      <Text fontSize="xs" fontWeight={600} color="blue.700" letterSpacing="0.5px" mb={1}>
        DRAFT AUTHORIZATION (§6.4.2)
      </Text>
      {atThreshold ? (
        <VStack align="stretch" spacing={2}>
          <Text fontSize="xs" color="blue.800">
            This encounter has reached the billing alert threshold ({daysSinceDOS}d since DOS) and
            the note has not been authenticated. Provider Billing Authorization is on file — you may
            submit under draft authorization.
          </Text>
          <Text fontSize="xs" color="blue.600" fontStyle="italic">
            Claim will be flagged &quot;Draft Authorization — Authentication Pending&quot; and the
            provider will be notified.
          </Text>
          <Button
            size="sm"
            colorScheme="blue"
            isLoading={submitting}
            isDisabled={!claims || claims.length === 0}
            onClick={handleSubmit}>
            Submit Under Draft Authorization
          </Button>
        </VStack>
      ) : (
        <Text fontSize="xs" color="blue.700">
          Provider Billing Authorization is on file. Draft authorization submission is available
          once the billing alert threshold is reached (
          {daysSinceDOS !== null
            ? `${daysSinceDOS}d since DOS — threshold: ${DEFAULT_BILLING_ALERT_THRESHOLD}d`
            : 'DOS unavailable'}
          ).
        </Text>
      )}
    </Box>
  )
}

// ─── §5.3: Delivery billing checklist ────────────────────────────────────────
// Per §0 compliance guardrail: all items checked before attestation is enabled.
// The billing team's signed attestation (with timestamp + named reviewer) is the
// audit document. This checklist guards that path — not a substitute for it.

const DELIVERY_CHECKLIST_ITEMS = [
  'Delivery type confirmed — matches claim type (vaginal / operative / cesarean transfer)',
  'Global OB vs. itemized billing confirmed — if global, antepartum claims reconciled',
  'Newborn claim routing confirmed — separate neonatal assessment claim reviewed if applicable',
  'Laceration repair reviewed — coded separately if documentation supports, or confirmed N/A',
]

const DeliveryBillingChecklist = ({
  hasAmendments,
  onChecklistComplete,
}: {
  hasAmendments: boolean
  onChecklistComplete: (complete: boolean) => void
}) => {
  const [checked, setChecked] = useState<boolean[]>(() =>
    Array(DELIVERY_CHECKLIST_ITEMS.length).fill(false),
  )
  const [amendmentReviewed, setAmendmentReviewed] = useState(!hasAmendments)

  const allComplete = checked.every(Boolean) && amendmentReviewed

  // Notify parent on every change
  const handleChange = useCallback(
    (index: number, value: boolean) => {
      setChecked(prev => {
        const next = [...prev]
        next[index] = value
        const complete = next.every(Boolean) && amendmentReviewed
        onChecklistComplete(complete)
        return next
      })
    },
    [amendmentReviewed, onChecklistComplete],
  )

  const handleAmendmentReviewed = useCallback(
    (value: boolean) => {
      setAmendmentReviewed(value)
      onChecklistComplete(checked.every(Boolean) && value)
    },
    [checked, onChecklistComplete],
  )

  return (
    <Box
      bg="yellow.50"
      border="1px solid"
      borderColor="yellow.300"
      borderRadius={6}
      px={3}
      py={2}
      _dark={{ bg: 'yellow.900', borderColor: 'yellow.700' }}>
      <Text fontSize="xs" fontWeight={600} color="yellow.800" letterSpacing="0.5px" mb={2}>
        DELIVERY BILLING REVIEW — ALL ITEMS REQUIRED BEFORE ATTESTATION
      </Text>
      <Alert status="info" borderRadius={4} py={1.5} fontSize="xs" mb={3}>
        <AlertIcon boxSize={3} />
        Per §0 compliance: this delivery attestation is the primary audit defense document for this
        claim. Retain with claim record — minimum 6 years.
      </Alert>
      <VStack align="stretch" spacing={2}>
        {DELIVERY_CHECKLIST_ITEMS.map((item, i) => (
          <Checkbox
            key={i}
            isChecked={checked[i]}
            onChange={e => handleChange(i, e.target.checked)}
            colorScheme="yellow"
            size="sm"
            alignItems="flex-start">
            <Text fontSize="sm" color="gray.700" pt={0.5}>
              {item}
            </Text>
          </Checkbox>
        ))}

        {/* Amendment log — auto-checked if no amendments, manual if amendments present */}
        <Checkbox
          isChecked={amendmentReviewed}
          onChange={e => handleAmendmentReviewed(e.target.checked)}
          isDisabled={!hasAmendments}
          colorScheme={hasAmendments ? 'yellow' : 'green'}
          size="sm"
          alignItems="flex-start">
          <Text fontSize="sm" color="gray.700" pt={0.5}>
            Amendment log reviewed — no post-signing edits affect code selection
            {!hasAmendments && (
              <Text as="span" color="green.600" ml={1}>
                (auto-checked — no amendments)
              </Text>
            )}
          </Text>
        </Checkbox>
      </VStack>
      {!allComplete && (
        <Text fontSize="xs" color="orange.600" mt={2}>
          Complete all checklist items to enable delivery attestation.
        </Text>
      )}
    </Box>
  )
}

// ─── Education checklist billing panel ─────────────────────────────────────────

interface ChecklistItemDoc {
  text: string
  gaWindow: GaWindow
  sortOrder: number
  createdOn: number
}

const EducationChecklistBillingPanel = ({ encounter }: { encounter: Encounter }) => {
  const assessmentId = encounter.assessmentId ?? null
  const data = (encounter.data ?? {}) as PrenatalExamData
  const thisVisitChecklist = data.patientEducationChecklist

  const checkedThisVisit = thisVisitChecklist?.items.filter(i => i.checked) ?? []

  const { dataArr: allEncounters } = useAssessmentExams(assessmentId, EncounterType.ANTEPARTUM)

  const lockedQuery = useMemo(() => {
    if (!thisVisitChecklist?.gaWindow) return null
    return query(
      collection(db, EDUCATION_CHECKLIST_LOCKED_ITEMS) as CollectionReference<ChecklistItemDoc>,
      where('gaWindow', '==', thisVisitChecklist.gaWindow),
    )
  }, [thisVisitChecklist?.gaWindow])
  const { dataArr: lockedItems } = useQuery<ChecklistItemDoc>(lockedQuery)

  const neverAddressed = useMemo(() => {
    if (!lockedItems || !allEncounters) return []
    return lockedItems.filter(
      locked =>
        !allEncounters.some(enc => {
          const cl = (enc.data as PrenatalExamData | undefined)?.patientEducationChecklist
          return cl?.items.some(i => i.itemId === locked.id && i.checked)
        }),
    )
  }, [lockedItems, allEncounters])

  if (!thisVisitChecklist) return null

  const showPanel = checkedThisVisit.length > 0 || neverAddressed.length > 0
  if (!showPanel) return null

  return (
    <Box bg="white" borderRadius={6} border="1px solid" borderColor="teal.200" px={3} py={2}>
      {checkedThisVisit.length > 0 && (
        <Box mb={neverAddressed.length > 0 ? 2 : 0}>
          <Text fontSize="xs" fontWeight={600} color="teal.700" letterSpacing="0.5px" mb={1}>
            EDUCATION DOCUMENTED THIS VISIT
          </Text>
          <VStack align="stretch" spacing={0.5}>
            {checkedThisVisit.map(item => (
              <HStack key={item.itemId}>
                {item.source === 'locked' && (
                  <Badge colorScheme="purple" fontSize="2xs">
                    Required
                  </Badge>
                )}
                <Text fontSize="xs">{item.itemText}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}
      {neverAddressed.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight={600} color="orange.700" letterSpacing="0.5px" mb={1}>
            REQUIRED TOPICS NOT YET ADDRESSED THIS EPISODE
          </Text>
          <VStack align="stretch" spacing={0.5}>
            {neverAddressed.map(item => (
              <Text key={item.id} fontSize="xs" color="orange.800">
                • {item.text}
              </Text>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  )
}

// ─── Billing actions panel (right panel) ──────────────────────────────────────

const BillingActionsPanel = ({
  encounter,
  examId,
  timeLogConfig,
  incompleteSubsequentDays,
  isBirthingCenter,
  laborCharting,
}: {
  encounter: Encounter
  examId: string
  timeLogConfig: TimeLogConfig | null
  incompleteSubsequentDays: string[]
  isBirthingCenter: boolean
  laborCharting: LaborCharting | null
}) => {
  const timeWorkLog = (encounter.data as any)?.timeWorkLog as TimeWorkLog | undefined
  const thresholds = timeLogConfig?.emThresholds ?? DEFAULT_EM_THRESHOLDS
  const intrapartumThresholds =
    timeLogConfig?.intrapartumThresholds ?? DEFAULT_INTRAPARTUM_THRESHOLDS
  const fieldLabels = timeLogConfig?.fieldLabels ?? {}
  const hasAmendments = (encounter.amendments?.length ?? 0) > 0
  const [deliveryChecklistComplete, setDeliveryChecklistComplete] = useState(false)

  const FACILITY_CLAIMS_REF = useMemo(
    () => collection(db, CLAIMS) as CollectionReference<FacilityClaim>,
    [],
  )
  const claimsQuery = useMemo(
    () => query(FACILITY_CLAIMS_REF, where('encounterId', '==', examId)),
    [FACILITY_CLAIMS_REF, examId],
  )
  const { dataArr: claims } = useQuery<FacilityClaim>(claimsQuery)

  // §6.4.2 + §6.8: Load admin practice data for POCUS credentialing and draft authorization.
  const { data: adminData } = useDocument<AdminPracticeData>(MIDWIVES_ADMIN, encounter.practiceId)

  // §6.6: Check for linked professional claims — required before attesting a facility claim
  // on a birthing center encounter. Professional claims are PregnancyClaims keyed by assessmentId.
  const professionalClaimsQuery = useMemo(
    () =>
      isBirthingCenter && encounter.assessmentId
        ? query(
            collection(db, CLAIMS) as CollectionReference<PregnancyClaim>,
            where('assessmentId', '==', encounter.assessmentId),
          )
        : null,
    [isBirthingCenter, encounter.assessmentId],
  )
  const { dataArr: professionalClaims } = useQuery<PregnancyClaim>(professionalClaimsQuery)

  // Block attestation when facility claims exist but no professional claim is linked.
  const linkedClaimBlocked =
    isBirthingCenter && (claims?.length ?? 0) > 0 && (professionalClaims?.length ?? 0) === 0

  // Resolve insurer from first available claim.
  const insurerId = useMemo(() => claims?.find(c => c.insurerId)?.insurerId ?? null, [claims])
  const { data: insurer } = useDocument<InsuranceProvider>(INSURANCE_PROVIDERS, insurerId)

  // Resolve payer requirements for the encounter's first CPT code.
  const payerRequirements = useMemo<string[]>(() => {
    if (!insurer?.requirementsByCodeType) return []
    const firstCpt = encounter.cptCodes?.[0] ?? null
    if (!firstCpt) return []
    const entry = insurer.requirementsByCodeType.find(
      (r: PayerCodeRequirement) => r.cptCode === firstCpt,
    )
    return entry?.requirements ?? []
  }, [insurer, encounter.cptCodes])

  const isPocus = encounter.type === EncounterType.ULTRASOUND
  const pocusReportAuthed = !!(encounter as any).pocusReportAuthentication

  return (
    <VStack align="stretch" spacing={3} h="100%" overflowY="auto" pl={1}>
      {/* §6.8: POCUS credentialing + TC/PC split + report auth — only for ultrasound encounters */}
      {isPocus && (
        <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
          <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.5px" mb={2}>
            POCUS BILLING (§6.8)
          </Text>
          <PocusCredentialingCheck adminData={adminData ?? null} encounterData={encounter.data} />
        </Box>
      )}

      {/* SCA banner */}
      {insurer?.singleCaseAgreementFlag && (
        <Alert status="warning" borderRadius={6} fontSize="sm">
          <AlertIcon />
          <Box>
            <Text fontWeight={600}>Single case agreement required</Text>
            <Text fontSize="xs">
              Verify the agreement is on file for this payer before submitting.
              {insurer.singleCaseAgreementDocUrl && (
                <>
                  {' '}
                  Agreement:{' '}
                  <a
                    href={insurer.singleCaseAgreementDocUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'underline' }}>
                    view document
                  </a>
                </>
              )}
            </Text>
          </Box>
        </Alert>
      )}

      {/* Denial triggers */}
      {insurer?.denialTriggers && insurer.denialTriggers.length > 0 && (
        <Box
          bg="orange.50"
          border="1px solid"
          borderColor="orange.200"
          borderRadius={6}
          px={3}
          py={2}>
          <Text fontSize="xs" fontWeight={600} color="orange.700" letterSpacing="0.5px" mb={1}>
            KNOWN DENIAL TRIGGERS — {insurer.name}
          </Text>
          <VStack align="stretch" spacing={0.5}>
            {insurer.denialTriggers.map((trigger, i) => (
              <Text key={i} fontSize="xs" color="orange.800">
                • {trigger}
              </Text>
            ))}
          </VStack>
        </Box>
      )}

      {/* §2.10.2: Auto-suggested modifiers — billing team only */}
      <SuggestedModifiersPanel encounter={encounter} />

      {/* Suggested E&M */}
      <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
        <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.5px" mb={2}>
          SUGGESTED E&amp;M CODE
        </Text>
        <SuggestedEmCode totalMinutes={timeWorkLog?.totalMinutes} thresholds={thresholds} />
      </Box>

      {/* §2.8.4 / §2.8.5: Possible additional billing codes — billing team only */}
      <AdditionalBillingFlags data={(encounter.data ?? {}) as FieldMapValue} />

      {/* §C (Addendum June 2026): Education checklist summary — antepartum only */}
      {encounter.type === EncounterType.ANTEPARTUM && (
        <EducationChecklistBillingPanel encounter={encounter} />
      )}

      {/* Time log */}
      <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
        <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.5px" mb={2}>
          TIME LOG
        </Text>
        {timeWorkLog ? (
          <TimeLogSummary timeWorkLog={timeWorkLog} fieldLabels={fieldLabels} />
        ) : (
          <Text fontSize="sm" color="gray.400" fontStyle="italic">
            No time log recorded
          </Text>
        )}
      </Box>

      {/* §4.3: Intrapartum time log — billing team only (per §0.3 compliance) */}
      {isBirthingCenter && laborCharting?.intrapartumTimeLog && (
        <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
          <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.5px" mb={2}>
            INTRAPARTUM TIME LOG
          </Text>
          <IntrapartumTimeLogSection
            laborCharting={laborCharting}
            intrapartumThresholds={intrapartumThresholds}
          />
        </Box>
      )}

      {/* §6.6: Supply log review — revenue code assignment for birthing center facility claims */}
      {isBirthingCenter && (
        <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
          <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.5px" mb={2}>
            SUPPLY LOG — REVENUE CODE ASSIGNMENT
          </Text>
          <SupplyLogReview encounter={encounter} examId={examId} />
        </Box>
      )}

      {/* §6.5: Hard block if subsequent-day documentation is incomplete */}
      {incompleteSubsequentDays.length > 0 && (
        <Alert status="error" borderRadius={6} fontSize="sm">
          <AlertIcon />
          <Box>
            <Text fontWeight={600}>Subsequent-day billing blocked</Text>
            <Text fontSize="xs">
              Documentation insufficient for subsequent-day billing on:{' '}
              {incompleteSubsequentDays
                .map(d =>
                  new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  }),
                )
                .join(', ')}
              . Provider must confirm vitals, FHR, and time log before these days can be billed.
            </Text>
          </Box>
        </Alert>
      )}

      {/* §6.4.2: Draft authorization — only shown for COMPLETED (not yet authenticated) encounters */}
      <DraftAuthorizationSection
        encounter={encounter}
        examId={examId}
        claims={claims ?? null}
        adminData={adminData ?? null}
      />

      {/* §6.6: Hard block when facility claim exists but no professional claim is linked */}
      {linkedClaimBlocked && (
        <Alert status="warning" borderRadius={6} fontSize="sm">
          <AlertIcon />
          <Box>
            <Text fontWeight={600}>No professional claim linked</Text>
            <Text fontSize="xs">
              Create a professional claim for this encounter before attesting the facility claim.
              Both claims must be linked under the same encounter date and episode.
            </Text>
          </Box>
        </Alert>
      )}

      {/* §5.3: Delivery billing checklist — required before attestation for birthing center encounters */}
      {isBirthingCenter && (
        <DeliveryBillingChecklist
          hasAmendments={hasAmendments}
          onChecklistComplete={setDeliveryChecklistComplete}
        />
      )}

      {/* Claim attestation */}
      <Box bg="white" borderRadius={6} border="1px solid" borderColor="gray.200" px={3} py={2}>
        <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.5px" mb={2}>
          CLAIM ATTESTATION
        </Text>
        {claims && claims.length > 0 ? (
          <VStack align="stretch" spacing={2}>
            {claims.map(claim => (
              <ClaimBillingActions
                key={claim.id}
                claim={claim}
                payerRequirements={payerRequirements}
                subsequentDayBlocked={incompleteSubsequentDays.length > 0}
                pocusReportBlocked={isPocus && !pocusReportAuthed}
                linkedClaimBlocked={linkedClaimBlocked}
                deliveryChecklistBlocked={isBirthingCenter && !deliveryChecklistComplete}
              />
            ))}
          </VStack>
        ) : (
          <Text fontSize="sm" color="gray.400" fontStyle="italic">
            No claims submitted for this encounter. Create a claim from the encounter record.
          </Text>
        )}
      </Box>
    </VStack>
  )
}

// ─── Main export ─────────────────────────────────────────────────────────────

export const ClaimReviewScreen = ({
  encounterId,
  patientRef,
}: {
  encounterId: string
  patientRef: DocumentReference<User> | null
}) => {
  const navigate = useNavigate()
  const { appName } = useApp()
  const { data: encounter, loading } = useDocument<Encounter>(ENCOUNTERS, encounterId)
  const { data: timeLogConfig } = useDocument<TimeLogConfig>(SETTINGS, TIME_LOG_CONFIG)

  // §6.5: Fetch labor charting for birthing center / admission encounters.
  const assessmentId = encounter?.assessmentId ?? null
  const isBirthingCenterType = encounter?.type === EncounterType.BIRTHING_CENTER_ADMISSION
  const isBirthingCenter =
    isBirthingCenterType || (encounter?.data as any)?.visitLocation?.location === 'birthing-center'
  const { data: laborCharting } = useDocument<LaborCharting>(
    LABOR_CHARTING,
    isBirthingCenterType ? assessmentId : null,
  )

  // Compute which subsequent days are missing complete documentation.
  const incompleteSubsequentDays = useMemo<string[]>(() => {
    if (!laborCharting) return []
    const entries = Object.values(laborCharting.entries ?? {})
    const dates = getLaborCalendarDates(
      entries as any,
      laborCharting.admission?.admittedAt ?? undefined,
    )
    return dates
      .slice(1)
      .filter(date => !isSubsequentDayComplete(laborCharting.subsequentDayNotes?.[date]))
  }, [laborCharting])

  // §6.2: Mark billing review timestamp once when the screen mounts. Firestore rules allow no
  // client writes to ENCOUNTERS, so this goes through the admin-only callable. Passive
  // background write (no user action, no optimistic UI to mislead) — fire-and-forget with a
  // warn on failure, matching the logEncounterView pattern below rather than a user-facing toast.
  useEffect(() => {
    updateEncounterBillingFields({ encounterId, billingReviewedOn: Date.now(), appName }).catch(
      err => console.warn('Failed to record billing review timestamp (§6.2)', err),
    )
  }, [encounterId, appName])

  // §7.1: opening this screen displays the full note content (renderSectionForBilling) —
  // a loggable access event. Once per claim open (this effect runs once per mount).
  // Fire-and-forget: a logging RTT must never gate claim review — failures only warn.
  useEffect(() => {
    logEncounterView({ encounterId, appName }).catch(err =>
      console.warn('Failed to log encounter view (§7.1)', err),
    )
  }, [encounterId, appName])

  if (loading) {
    return (
      <Center w="100%" h="100%">
        <Spinner color="green.400" size="lg" />
      </Center>
    )
  }

  if (!encounter) {
    return (
      <Center w="100%" h="100%">
        <Text color="gray.500">Encounter not found.</Text>
      </Center>
    )
  }

  return (
    <Flex flexFlow="column" w="100%" h="100%" bg="gray.50">
      {/* Header bar */}
      <HStack
        w="100%"
        px={4}
        py={2}
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        spacing={3}>
        <IconButton
          icon={<ArrowBackIcon />}
          aria-label="Back to billing dashboard"
          size="sm"
          variant="ghost"
          onClick={() => navigate('/billing')}
        />
        <Text fontWeight={600} color="gray.700">
          Claim Review
        </Text>
        <Badge colorScheme="gray" fontSize="xs">
          {encounterTypeName[encounter.type] ?? encounter.type}
        </Badge>
        <Text fontSize="sm" color="gray.500">
          {encounter.date ?? '—'}
        </Text>
      </HStack>

      {/* Split panel body */}
      <Flex flex={1} minH={0} p={4} gap={4} overflow="hidden">
        {/* Left — clinical summary */}
        <Box flex={1} minW={0} h="100%" overflow="auto">
          <EncounterSummaryPanel
            patientRef={patientRef}
            encounter={encounter}
            encounterId={encounterId}
            laborCharting={laborCharting ?? null}
          />
        </Box>

        <Divider orientation="vertical" />

        {/* Right — billing actions */}
        <Box w="380px" flexShrink={0} h="100%" overflow="auto">
          <BillingActionsPanel
            encounter={encounter}
            examId={encounterId}
            timeLogConfig={timeLogConfig}
            incompleteSubsequentDays={incompleteSubsequentDays}
            isBirthingCenter={isBirthingCenter ?? false}
            laborCharting={laborCharting ?? null}
          />
        </Box>
      </Flex>
    </Flex>
  )
}
